import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import clsx from 'clsx'

import type { BadgeProps } from '@wartek-id/badge'
import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { DialogUpdateSatuanPendidikanInfo } from 'app/KinerjaPSPegawaiPage/components/DialogUpdateSatuanPendidikanInfo'
import {
  SCORE_COLORS,
  SCORE_TRANSLATION,
} from 'app/KinerjaPSPegawaiPage/constants/praktikKinerja'

import type {
  TPraktikKinerjaItem,
  TSatuanPendidikanV2Item,
} from 'types/kinerja-ps-pegawai'

type PilihSatuanPendidikanProps = {
  satuanPendidikanData: TSatuanPendidikanV2Item[]
  praktikKinerjaData?: TPraktikKinerjaItem[]
}

const PilihSatuanPendidikan = ({
  satuanPendidikanData,
  praktikKinerjaData = [],
}: PilihSatuanPendidikanProps) => {
  const { control } = useFormContext()
  const [isDialogUpdateInfoOpen, setDialogUpdateInfoOpen] = useState(false)

  const renderScoreBadge = (scoreMetric: keyof typeof SCORE_COLORS) => {
    return (
      <Badge variant={SCORE_COLORS[scoreMetric] as BadgeProps['variant']}>
        {SCORE_TRANSLATION[scoreMetric]}
      </Badge>
    )
  }

  const renderScores = (scores: TSatuanPendidikanV2Item['scores']) => {
    // build a lookup for quick access, compare ids as strings for safety
    const scoreById = new Map(scores.map((s) => [String(s.id), s]))
    // order scores according to praktikKinerjaData order; keep gaps as '-'
    const ordered = praktikKinerjaData.map(
      (pk) => scoreById.get(String(pk.id)) ?? null
    )
    return ordered.map((item, idx) => (
      <td key={item?.id ?? `missing-${idx}`} className="p-4">
        {item ? renderScoreBadge(item.score) : '-'}
      </td>
    ))
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Text variant="heading-lg">
          Berikut adalah seluruh satdik yang menjadi fokus dalam melakukan
          Praktik Kinerja Anda
        </Text>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Text>Berikut {satuanPendidikanData.length} satdik binaan Anda:</Text>
          <Button
            variant="ghost"
            className="space-x-1"
            onClick={() => setDialogUpdateInfoOpen(true)}
            size="sm"
          >
            <Icon color="informational" fontSize="sm">
              help
            </Icon>
            <span>Cara perbarui data satdik</span>
          </Button>
        </div>
      </div>

      <table className="w-full">
        <thead className="font-bold border-b bg-surface-subdued border-subdued">
          <tr>
            <td className="p-4">Nama Satuan Pendidikan</td>
            {praktikKinerjaData.map((item, index) => (
              <td key={index} className="p-4">
                {item.nama}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          <Controller
            control={control}
            name="selectedSatuanPendidikanIds"
            rules={{
              required: true,
            }}
            render={({ field: { value } }) => {
              return (
                <>
                  {satuanPendidikanData.map(({ npsn, name, scores }, index) => {
                    const isSelected = value.includes(npsn)
                    return (
                      <tr
                        key={index}
                        className={clsx(
                          'border-b border-subdued',
                          isSelected && '!bg-surface-selected-default'
                        )}
                      >
                        <td className="p-4">{name}</td>
                        {renderScores(scores)}
                      </tr>
                    )
                  })}
                </>
              )
            }}
          />
        </tbody>
      </table>
      <DialogUpdateSatuanPendidikanInfo
        isOpen={isDialogUpdateInfoOpen}
        onClose={() => setDialogUpdateInfoOpen(false)}
      />
    </div>
  )
}

export default PilihSatuanPendidikan
